import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useMutation } from "@tanstack/react-query";
import { toastMessage } from "@/utils/helper/myToast";
import { AbsoluteCenter } from "@/common/css";

interface ResendOTPProps {
  resendOtpAPI: () => Promise<unknown>;
  className?: string;
  errorMessage?: string;
  resetErrorMessage: () => void;
  text?: string;
  linkText?: string;
  setResetCount?: any;
}

const ResendOTP: React.FC<ResendOTPProps> = ({
  resendOtpAPI,
  className = "",
  errorMessage,
  resetErrorMessage,
  linkText = "Resend code",
  text = "Didn't receive a code?",
  setResetCount,
}) => {
  const [countdown, setCountdown] = useState(30);
  const [isDisabled, setIsDisabled] = useState(true);
  const mutation = useMutation({
    mutationFn: resendOtpAPI,
    onSuccess: (res: any) => {
      if (res.status === 200) {
        // Reset countdown on success
        setIsDisabled(true);
        setCountdown(30);
        if (setResetCount) setResetCount((pre: any) => pre + 1);
        if (errorMessage) resetErrorMessage();
        const { message, isError, showMessage } = res.data;
        if (showMessage) toastMessage(message, isError);
      } else {
        const { message, isError } = res?.response?.data || {};
        if (message) {
          return toastMessage(message, isError);
        }
      }
    },
    onError: (error: any) => {
      console.log(error);
      if (error.response?.data?.showMessage) {
        console.log({ error });
      }
    },
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isDisabled && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
    } else if (countdown === 0) {
      setIsDisabled(false);
      setCountdown(30);
    }
    return () => clearInterval(timer);
  }, [isDisabled, countdown]);

  const handleResend = () => mutation.mutate();

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    let text = `${seconds.toString().padStart(2, "0")}`;
    if (minutes) {
      text = `${minutes.toString().padStart(2, "0")}:${text}`;
    } else {
      text = text + " sec";
    }
    return text;
  };

  return (
    <div className={`mt-3 w-full font-normal text-text-label !leading-normal text-13 ${className}`}>
      <span> {text} </span>
      <motion.button
        onClick={handleResend}
        disabled={isDisabled || mutation.status === "pending"}
        className={`font-medium ${isDisabled ? "opacity-50 cursor-not-allowed" : "hover:underline disabled:text-text-label text-text-link relative"}`}
        whileHover={!isDisabled ? { scale: 1.05 } : {}}
        whileTap={!isDisabled ? { scale: 0.95 } : {}}
      >
        {mutation.status === "pending" && (
          <AbsoluteCenter className=" bg-gray-100 opacity-75 w-full flex items-center justify-center">
            <div className="w-3.5 h-3.5 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
          </AbsoluteCenter>
        )}
        {linkText}
      </motion.button>
      {isDisabled && (
        <motion.span initial={{ opacity: 0, x: -100 }} animate={{ opacity: 1, x: 0 }} className="px-1">
          in <span className=" text-text-primary">{formatTime(countdown)}</span>
        </motion.span>
      )}
    </div>
  );
};

export default ResendOTP;
